import { AdminDashboardsGetPigletRegistrationOverviewParams } from '../api/HealthSystemApi';
import { ApiService } from './ApiService';
import { FarmsOverview, FarmsOverviewQuery } from '../models/farms-overview';

/**
 * `Status`, `Weighing`, `PendingOnly` and `NoAntibioticsOnly` are intersected in rather than coming from the generated client, for
 * the same reason `FarmOverviewRow.status` is - see `models/farms-overview`. Declared as a named type rather
 * than passed inline so the call below is still checked against the generated shape.
 */
type OverviewParams = AdminDashboardsGetPigletRegistrationOverviewParams & {
  Status?: string;
  Weighing?: string;
  PendingOnly?: boolean;
  NoAntibioticsOnly?: boolean;
};

/**
 * Admin farms overview behind `/admin/farms-overview`.
 *
 * Thin wrapper over the generated client's `GET /admin/dashboard/piglets`. Unlike its sibling
 * `PigletRegistrationService`, which is hand-written against `BaseService`, this endpoint is in the
 * generated client already, so there is nothing to hand-write.
 *
 * The one thing the wrapper earns: the API pages from 1 and the UI pages from 0, and empty filters have to
 * be dropped rather than sent as blanks, since `Search=` and `Rule=` are not the same request as sending
 * neither. Worst first goes out the same way - omitted, not `Sort=severity` - since that is the order the API
 * falls back to on its own.
 */
export class FarmsOverviewService extends ApiService {
  getOverview = async (query: FarmsOverviewQuery): Promise<FarmsOverview> => {
    const search = query.searchText.trim();

    const params: OverviewParams = {
      Search: search || undefined,
      Rule: query.rule ?? undefined,
      Severity: query.severity ?? undefined,
      Status: query.status ?? undefined,
      Weighing: query.weighing ?? undefined,
      PendingOnly: query.pendingOnly || undefined,
      NoAntibioticsOnly: query.noAntibioticsOnly || undefined,
      Sort: query.sort ?? undefined,
      Page: query.page + 1,
      Size: query.size
    };

    const result = await this.api.adminDashboards.adminDashboardsGetPigletRegistrationOverview(params);

    // The generated client predates the production cycle fields; `FarmsOverview` describes the
    // response the API sends now. Drop the cast when the client is regenerated.
    return result.data as unknown as FarmsOverview;
  };
}

export default FarmsOverviewService;
